import { getCard } from '@lafayette/content';
import type { GameProjection, PayableAsset } from '@lafayette/protocol';

import { nameOf, titleOf } from './selectors.js';

export interface PaymentPlan {
  debtId: string;
  amount: number;
  creditorId: string;
  eligible: PayableAsset[];
  selected: string[];
  /** Value of everything selected so far. */
  total: number;
  /** Value of everything the debtor could possibly hand over. */
  available: number;
  /** True when the debtor cannot cover it and must give up everything. */
  mustGiveEverything: boolean;
  /** True when the selection is one the engine will accept. */
  ready: boolean;
  /** Cards that can be added or removed right now. */
  toggleableIds: string[];
}

function assetsById(eligible: PayableAsset[]): Map<string, PayableAsset> {
  return new Map(eligible.map((asset) => [asset.instanceId, asset]));
}

/**
 * A mall has to come apart in a fixed order, so within one mall only the next
 * card in that order can be added, and only the last one taken back.
 */
function toggleable(eligible: PayableAsset[], selected: string[]): string[] {
  const chosen = new Set(selected);
  const ids: string[] = [];

  const byGroup = new Map<string, PayableAsset[]>();
  for (const asset of eligible) {
    if (asset.from === 'bank') {
      ids.push(asset.instanceId);
      continue;
    }
    const list = byGroup.get(asset.group!) ?? [];
    list.push(asset);
    byGroup.set(asset.group!, list);
  }

  for (const list of byGroup.values()) {
    const ordered = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const taken = ordered.filter((asset) => chosen.has(asset.instanceId)).length;
    // The next card down can be added; the deepest one taken can be put back.
    if (ordered[taken]) ids.push(ordered[taken]!.instanceId);
    if (taken > 0) ids.push(ordered[taken - 1]!.instanceId);
  }
  return ids;
}

export function paymentPlan(
  projection: GameProjection,
  command: { debtId: string; amount: number; eligible: PayableAsset[] },
  selected: string[],
): PaymentPlan {
  const byId = assetsById(command.eligible);
  const kept = selected.filter((id) => byId.has(id));
  const total = kept.reduce((sum, id) => sum + (byId.get(id)?.value ?? 0), 0);
  const available = command.eligible.reduce((sum, asset) => sum + asset.value, 0);
  const mustGiveEverything = available < command.amount;
  const debt = projection.debts.find((d) => d.id === command.debtId);

  return {
    debtId: command.debtId,
    amount: command.amount,
    creditorId: debt?.creditorId ?? '',
    eligible: command.eligible,
    selected: kept,
    total,
    available,
    mustGiveEverything,
    ready: mustGiveEverything ? kept.length === command.eligible.length : total >= command.amount,
    toggleableIds: toggleable(command.eligible, kept),
  };
}

/** Adds or removes a card, keeping each mall's dismantle order intact. */
export function togglePayment(plan: PaymentPlan, instanceId: string): string[] {
  if (plan.selected.includes(instanceId)) {
    return plan.selected.filter((id) => id !== instanceId);
  }
  return [...plan.selected, instanceId];
}

/**
 * Why this card cannot go into the payment right now. Shown in the card's own
 * detail view so a rejected pick explains itself.
 */
export function explainNotPayable(
  projection: GameProjection,
  plan: PaymentPlan,
  instanceId: string,
): string {
  const definitionId = projection.cards[instanceId];
  const title = titleOf(projection, instanceId);

  const mine =
    projection.self.bank.includes(instanceId) ||
    Object.values(projection.self.malls).some((mall) => mall?.cards.includes(instanceId));
  if (!mine) {
    return `You can only pay with your own cards. ${title} belongs to someone else.`;
  }

  if (definitionId) {
    const definition = getCard(definitionId);
    if (definition.kind === 'propertyWildcard' && definition.groups.length > 2) {
      return `${title} has no cash value, so it can never settle a debt.`;
    }
  }

  const asset = plan.eligible.find((a) => a.instanceId === instanceId);
  if (!asset) return `${title} cannot be used to pay.`;

  // It is eligible, just not next in its mall's dismantle order.
  const mall = plan.eligible
    .filter((a) => a.group === asset.group && a.from === 'mall')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const blocking = mall.find(
    (a) => (a.order ?? 0) < (asset.order ?? 0) && !plan.selected.includes(a.instanceId),
  );
  if (blocking) {
    return `Hand over ${titleOf(projection, blocking.instanceId)} from this strip mall first.`;
  }
  return `Take back ${titleOf(projection, mall.at(-1)?.instanceId ?? '')} before changing this one.`;
}

/** "You owe Ana $5M." */
export function debtSentence(projection: GameProjection, plan: PaymentPlan): string {
  const creditor = plan.creditorId ? nameOf(projection, plan.creditorId) : 'another player';
  return `You owe ${creditor} $${plan.amount}M.`;
}
